import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Divider,
  FormLabel,
  Stack,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import {
  detailsPLSchedules,
  investmentDetailsPLSchedules,
  investmentTaxAdjustmentOptions,
  taxAdjustmentOptions,
} from "../../../utils/static/detailsPL";
import DialogLoading from "../../App/DialogLoading";
import GroupedSelect from "../GroupedSelect";
import RequiredChip from "../Form/RequiredChip";
import { TextFieldForName } from "../TextField/TextField";
import BaseDialog from "./BaseDialog";

const AUTO_TITLED_SCHEDULES = ["Allocation Of Expenses", "Renovation"];

const ScheduleCreationDialog = ({
  company,
  open,
  onClose,
  onSubmit,
  onOpenSchedule,
  title,
  isLoading,
  formData,
  onFormChange,
}) => {
  const [createdSchedule, setCreatedSchedule] = useState(null);

  const PLScheduleTypes = useMemo(() => {
    const scheduleMap =
      company.companyType === "investment"
        ? investmentDetailsPLSchedules
        : detailsPLSchedules;
    const scheduleKeys = Object.keys(scheduleMap);
    const adjustmentOptions =
      company.companyType === "investment"
        ? investmentTaxAdjustmentOptions
        : taxAdjustmentOptions;

    return adjustmentOptions
      .filter(
        (option) =>
          scheduleKeys.includes(option.value) && option.value !== "FAAdditions"
      )
      .map((option) => ({
        value: scheduleMap[option.value],
        label: option.label,
        group: option.group,
      }));
  }, [company.companyType]);

  useEffect(() => {
    if (!open) setCreatedSchedule(null);
  }, [open]);

  const handleScheduleTypeChange = (scheduleType) => {
    onFormChange("scheduleType", scheduleType);

    if (scheduleType === "Renovation") {
      onFormChange(
        "scheduleTitle",
        "Renovation and Refurbishment Deductions Under S14N"
      );
    } else if (scheduleType === "Allocation Of Expenses") {
      onFormChange("scheduleTitle", "Allocation Of Expenses");
    } else {
      onFormChange("scheduleTitle", "");
    }
  };

  const handleClose = () => {
    if (!isLoading) onClose?.();
  };

  const handleSubmit = async () => {
    const schedule = await onSubmit?.();
    if (schedule) setCreatedSchedule(schedule);
  };

  const isFormValid =
    formData.scheduleType &&
    (AUTO_TITLED_SCHEDULES.includes(formData.scheduleType) ||
      formData.scheduleTitle?.trim());
  const isTitleDisabled =
    !formData.scheduleType ||
    AUTO_TITLED_SCHEDULES.includes(formData.scheduleType);
  const showRequiredForTitle =
    formData.scheduleType &&
    !AUTO_TITLED_SCHEDULES.includes(formData.scheduleType);

  const helperText = !formData.scheduleType
    ? "Select a schedule type before entering its title."
    : isTitleDisabled
      ? "The title is set automatically for this schedule type."
      : "Use a clear title that will be easy to identify in the schedule list.";

  const actions = createdSchedule ? (
    <>
      <Button onClick={handleClose} variant='outlined'>
        Close
      </Button>
      <Button
        onClick={() => onOpenSchedule?.(createdSchedule)}
        variant='contained'
        endIcon={<ArrowForwardIcon />}
      >
        Open schedule
      </Button>
    </>
  ) : (
    <>
      <Button onClick={handleClose} disabled={isLoading} variant='outlined'>
        Cancel
      </Button>
      <Button
        onClick={handleSubmit}
        disabled={isLoading || !isFormValid}
        variant='contained'
      >
        {isLoading ? "Creating..." : "Create schedule"}
      </Button>
    </>
  );

  return (
    <BaseDialog
      open={open}
      onClose={handleClose}
      closeDisabled={isLoading}
      maxWidth='sm'
      title={createdSchedule ? "Schedule created" : title}
      subtitle={
        createdSchedule
          ? "The new schedule is ready to use."
          : "Add a schedule to this tax computation."
      }
      icon={<AddIcon fontSize='small' />}
      actions={actions}
      paperSx={{
        maxHeight: "calc(100dvh - 32px)",
        overflow: "hidden",
      }}
    >
      {isLoading ? (
        <Stack
          alignItems='center'
          justifyContent='center'
          spacing={1.5}
          sx={{ minHeight: 220, py: 3 }}
        >
          <DialogLoading />
          <Typography variant='body2' color='text.secondary'>
            Setting up your schedule...
          </Typography>
        </Stack>
      ) : createdSchedule ? (
        <Stack spacing={2.5} alignItems='center' sx={{ py: { xs: 1, sm: 2 } }}>
          <Box
            sx={{
              alignItems: "center",
              bgcolor: "success.lighter",
              borderRadius: "50%",
              color: "success.main",
              display: "flex",
              height: 64,
              justifyContent: "center",
              width: 64,
            }}
          >
            <CheckCircleOutlineIcon sx={{ fontSize: 36 }} />
          </Box>

          <Box sx={{ textAlign: "center", width: "100%" }}>
            <Typography variant='h6' fontWeight={700} gutterBottom>
              {createdSchedule.scheduleTitle}
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              You can open it now and start adding schedule details.
            </Typography>
          </Box>

          <Box
            sx={{
              bgcolor: "background.default",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              px: 2,
              py: 1.5,
              width: "100%",
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              divider={<Divider flexItem orientation='vertical' />}
              spacing={{ xs: 1, sm: 2 }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant='caption' color='text.secondary'>
                  Schedule number
                </Typography>
                <Typography variant='body2' fontWeight={700}>
                  {createdSchedule.scheduleNo || "Assigned"}
                </Typography>
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant='caption' color='text.secondary'>
                  Schedule type
                </Typography>
                <Typography
                  variant='body2'
                  fontWeight={700}
                  sx={{ overflowWrap: "anywhere" }}
                >
                  {createdSchedule.scheduleType || formData.scheduleType}
                </Typography>
              </Box>
            </Stack>
          </Box>
        </Stack>
      ) : (
        <Stack spacing={2.5}>
          <Box>
            <FormLabel
              component='legend'
              sx={{
                alignItems: "center",
                color: "text.primary",
                display: "flex",
                fontWeight: 600,
                gap: 0.75,
                mb: 1,
              }}
            >
              Schedule type <RequiredChip />
            </FormLabel>
            <GroupedSelect
              value={formData.scheduleType || ""}
              onChange={(event) => handleScheduleTypeChange(event.target.value)}
              options={PLScheduleTypes}
              placeholder='Select schedule type'
              sx={{
                width: "100%",
                "& .MuiSelect-select": {
                  alignItems: "center",
                  display: "flex",
                  minHeight: "24px !important",
                },
              }}
            />
          </Box>

          <Box>
            <FormLabel
              component='legend'
              sx={{
                alignItems: "center",
                color: "text.primary",
                display: "flex",
                fontWeight: 600,
                gap: 0.75,
                mb: 1,
              }}
            >
              Schedule title
              {showRequiredForTitle && <RequiredChip />}
            </FormLabel>
            <TextFieldForName
              value={formData.scheduleTitle || ""}
              onChange={(value) => onFormChange("scheduleTitle", value)}
              charLimit={60}
              disabled={isTitleDisabled}
              placeholder={
                isTitleDisabled
                  ? "Auto-filled based on schedule type"
                  : "Enter schedule title"
              }
              size='small'
              sx={{ width: "100%" }}
            />
            <Typography
              variant='caption'
              color='text.secondary'
              sx={{ display: "block", lineHeight: 1.4, mt: 0.75 }}
            >
              {helperText}
            </Typography>
          </Box>
        </Stack>
      )}
    </BaseDialog>
  );
};

export default ScheduleCreationDialog;
